import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Copy, CheckCircle2, Server, UserCheck, Zap, Code2, ShieldAlert, RefreshCw, Terminal, Link, Save, ArrowRightLeft, Database, Users, DownloadCloud } from "lucide-react";
import { REACT_APP_API_URL } from "config/api-config";

export default function HRMSSetup({ onClose }) {
  const [activeTab, setActiveTab] = useState("inbound");

  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  
  const [apiKey, setApiKey] = useState("Loading...");
  const [allowedIps, setAllowedIps] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookAuthToken, setWebhookAuthToken] = useState("");
  const [autoSyncOutbound, setAutoSyncOutbound] = useState(false);
  const [savingOutbound, setSavingOutbound] = useState(false);

  // Database Fetch state
  const [pullUrl, setPullUrl] = useState("");
  const [pullToken, setPullToken] = useState("");
  const [fetching, setFetching] = useState(false);
  const [savingFetch, setSavingFetch] = useState(false);

  // The new endpoint from your staff.py router
  const apiUrl = `${REACT_APP_API_URL}/staff/hrms/sync_staff`;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get(`${REACT_APP_API_URL}/events/hrms/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setApiKey(res.data.inbound_api_key || "Loading...");
        setAllowedIps(res.data.allowed_ips || "");
        setWebhookUrl(res.data.webhook_url || "");
        setWebhookAuthToken(res.data.api_key || ""); // using api_key field for outbound token
        setAutoSyncOutbound(res.data.auto_sync !== false);
        setPullUrl(res.data.pull_url || "");
        setPullToken(res.data.pull_token || "");
      } catch (error) {
        toast.error("Failed to load HRMS settings.");
      }
    };
    fetchSettings();
  }, []);

  const handleSaveSecurity = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/events/hrms/config`, 
        { allowed_ips: allowedIps }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Security settings saved!");
    } catch (error) {
      toast.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOutbound = async () => {
    setSavingOutbound(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/events/hrms/config`, 
        { 
          webhook_url: webhookUrl,
          api_key: webhookAuthToken,
          auto_sync: autoSyncOutbound
        }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Outbound Webhook settings saved!");
    } catch (error) {
      toast.error("Failed to save webhook settings.");
    } finally {
      setSavingOutbound(false);
    }
  };

  const handleSaveFetchConfig = async () => {
    setSavingFetch(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/events/hrms/config`, 
        { 
          pull_url: pullUrl,
          pull_token: pullToken
        }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Database Fetch Config saved!");
    } catch (error) {
      toast.error("Failed to save config.");
    } finally {
      setSavingFetch(false);
    }
  };

  const handleFetchStaff = async () => {
    setFetching(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/staff/hrms/fetch_database`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.detail);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync HRMS database.");
    } finally {
      setFetching(false);
    }
  };

  const handleResetKey = async () => {
    if(!window.confirm("Are you sure? This will instantly break your current integrations until you update them with the new key.")) {
      return;
    }
    setResetting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/events/hrms/reset_key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKey(res.data.inbound_api_key); 
      toast.success("Old key destroyed! New key generated.");
    } catch (error) {
      toast.error("Failed to reset API key.");
    } finally {
      setResetting(false);
    }
  };

  const jsonPayload = `{
  "staff_id": "EMP-9001",
  "first_name": "Jane",
  "last_name": "Smith",
  "email": "jane.smith@example.com",
  "mobile_no": "9876543210",
  "department": "Engineering",
  "images": [
    "/9j/4AAQSkZJRgABAQEAAAAAAAD...",
    "/9j/4AAQSkZJRgABAQEAAAAAAAD..."
  ]
}`;

  const curlCommand = `curl -X POST ${apiUrl} \\
-H "Content-Type: application/json" \\
-H "x-api-key: ${apiKey}" \\
-d '{
  "staff_id": "EMP-9001", 
  "first_name": "Jane", 
  "last_name": "Smith", 
  "email": "jane.smith@example.com", 
  "mobile_no": "9876543210", 
  "department": "Engineering", 
  "images": ["<base64_string_1>", "<base64_string_2>"]
}'`;

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  return (
    <div style={{ backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "20px", boxShadow: "0px 4px 25px rgba(0,0,0,0.05)", maxHeight: "90vh", overflowY: "auto" }}>
      
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "25px", borderBottom: "1px solid #F4F7FE", paddingBottom: "20px" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "14px", backgroundColor: "#e6f5ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Users size={28} color="#0088cc" />
        </div>
        <div>
          <h3 style={{ margin: 0, color: "#2B3674", fontSize: "20px", fontWeight: "700" }}>HRMS Integration API</h3>
          <p style={{ margin: 0, color: "#A3AED0", fontSize: "14px" }}>Automate staff directory and attendance logging.</p>
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0", marginBottom: "25px", gap: "10px" }}>
        <button 
          onClick={() => setActiveTab("inbound")}
          style={{ padding: "12px 16px", background: "none", border: "none", borderBottom: activeTab === "inbound" ? "3px solid #0088cc" : "3px solid transparent", color: activeTab === "inbound" ? "#0088cc" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <Database size={18} /> Push Staff TO CamSec
        </button>
        <button 
          onClick={() => setActiveTab("fetch")}
          style={{ padding: "12px 16px", background: "none", border: "none", borderBottom: activeTab === "fetch" ? "3px solid #0088cc" : "3px solid transparent", color: activeTab === "fetch" ? "#0088cc" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <DownloadCloud size={18} /> Pull DB FROM HRMS
        </button>
        <button 
          onClick={() => setActiveTab("outbound")}
          style={{ padding: "12px 16px", background: "none", border: "none", borderBottom: activeTab === "outbound" ? "3px solid #0088cc" : "3px solid transparent", color: activeTab === "outbound" ? "#0088cc" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <ArrowRightLeft size={18} /> Send Logs TO HRMS
        </button>
      </div>

      {/* 1. INBOUND TAB */}
      {activeTab === "inbound" && (
        <div style={{ animation: "fadeIn 0.3s ease-out" }}>
          <div style={{ marginBottom: "25px", backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #0088cc" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>What does this do?</h4>
            <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
              Normally, you have to register employees manually. By giving the API details below to your IT team, your HRMS can automatically push employee details and profile photos into CamSec Ai the moment they are onboarded in your primary system.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div style={{ minWidth: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#e6f5ff", color: "#0088cc", display: "flex", alignItems: "center", justifyContent: "center" }}><Server size={20}/></div>
              <div>
                <h5 style={{ margin: "0 0 2px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700" }}>1. Onboard Employee</h5>
                <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>Add a new staff member in your native HRMS.</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div style={{ minWidth: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#e9fced", color: "#25D366", display: "flex", alignItems: "center", justifyContent: "center" }}><Zap size={20}/></div>
              <div>
                <h5 style={{ margin: "0 0 2px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700" }}>2. Auto-Sync</h5>
                <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>Your software pushes their profile + base64 photos to CamSec Ai.</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div style={{ minWidth: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#f0ebff", color: "#4318FF", display: "flex", alignItems: "center", justifyContent: "center" }}><UserCheck size={20}/></div>
              <div>
                <h5 style={{ margin: "0 0 2px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700" }}>3. Ready for Attendance</h5>
                <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>Facial AI is processed instantly. They can mark attendance immediately.</p>
              </div>
            </div>
          </div>

          {/* SECURITY CONFIGURATION */}
          <div style={{ borderTop: "1px dashed #E2E8F0", paddingTop: "25px", marginBottom: "20px" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
              <ShieldAlert size={18} color="#EE5D50" /> Security Configuration
            </h4>
            <p style={{ margin: "0 0 15px 0", color: "#A3AED0", fontSize: "13px", lineHeight: "1.5" }}>
              Only servers with the secret API Key can push employee data. For strict security, restrict access to specific IP addresses.
            </p>

            <div style={{ marginBottom: "15px" }}>
              <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: "#2B3674", fontWeight: "700" }}>YOUR SECRET API KEY</p>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                <input type="text" readOnly value={apiKey} style={{ flexGrow: 1, minWidth: "250px", padding: "10px", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "14px", backgroundColor: "#F8FAFC", color: "#EE5D50", fontWeight: "600", letterSpacing: "1px" }} />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => copyToClipboard(apiKey, setCopiedKey)} style={{ padding: "10px 16px", backgroundColor: "#F4F7FE", color: "#4318FF", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>
                    {copiedKey ? "Copied" : "Copy"}
                  </button>
                  <button onClick={handleResetKey} disabled={resetting} style={{ padding: "10px 16px", backgroundColor: "#ffeeec", color: "#EE5D50", border: "none", borderRadius: "8px", cursor: resetting ? "not-allowed" : "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                    <RefreshCw size={16} className={resetting ? "animate-spin" : ""} />
                    {resetting ? "Resetting..." : "Reset Key"}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: "#2B3674", fontWeight: "700" }}>WHITELISTED IP ADDRESSES (OPTIONAL)</p>
              <input type="text" placeholder="e.g. 203.0.113.45, 198.51.100.12" value={allowedIps} onChange={(e) => setAllowedIps(e.target.value)} style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "14px", outline: "none", color: "#2B3674" }} />
            </div>
            <button onClick={handleSaveSecurity} disabled={saving} style={{ padding: "8px 16px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
              {saving ? "Saving..." : "Save Security Rules"}
            </button>
          </div>

          <div style={{ borderTop: "1px dashed #E2E8F0", paddingTop: "25px", marginBottom: "20px" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>For Your IT Developer:</h4>
            <p style={{ margin: "0 0 15px 0", color: "#A3AED0", fontSize: "13px", lineHeight: "1.5" }}>
              Send a <b>POST</b> request to the Webhook URL below. You must include the header: <code>x-api-key: YOUR_SECRET_KEY</code>. Pass an array of base64 strings in the `images` key for facial AI training.
            </p>

            <div style={{ backgroundColor: "#F8FAFC", padding: "16px", borderRadius: "12px", border: "1px solid #E2E8F0", marginBottom: "20px" }}>
              <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#A3AED0", fontWeight: "700" }}>WEBHOOK ENDPOINT URL</p>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <code style={{ flexGrow: 1, color: "#4318FF", fontSize: "14px", fontWeight: "600", wordBreak: "break-all" }}>{apiUrl}</code>
                <button onClick={() => copyToClipboard(apiUrl, setCopiedUrl)} style={{ padding: "8px 12px", backgroundColor: "#F4F7FE", color: "#4318FF", border: "none", borderRadius: "8px", cursor: "pointer" }}><Copy size={16} /></button>
              </div>
            </div>

            <div style={{ backgroundColor: "#0B1437", padding: "16px", borderRadius: "12px", marginBottom: "20px", border: "1px solid #2B3674" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#A3AED0", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}><Terminal size={14} /> cURL TEST COMMAND</p>
                <button onClick={() => copyToClipboard(curlCommand, setCopiedCurl)} style={{ background: "none", border: "none", color: "#00E396", cursor: "pointer", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedCurl ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copiedCurl ? "COPIED" : "COPY CMD"}
                </button>
              </div>
              <pre style={{ margin: 0, padding: "12px", backgroundColor: "#111C44", color: "#00E396", borderRadius: "8px", fontSize: "13px", fontFamily: "monospace", overflowX: "auto" }}>{curlCommand}</pre>
            </div>

            <div style={{ backgroundColor: "#111C44", padding: "16px", borderRadius: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#A3AED0", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}><Code2 size={14} /> JSON PAYLOAD FORMAT</p>
                <button onClick={() => copyToClipboard(jsonPayload, setCopiedJson)} style={{ background: "none", border: "none", color: "#00E396", cursor: "pointer", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedJson ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copiedJson ? "COPIED" : "COPY JSON"}
                </button>
              </div>
              <pre style={{ margin: 0, padding: "15px", backgroundColor: "#0B1437", color: "#00E396", borderRadius: "8px", fontSize: "13px", fontFamily: "monospace", overflowX: "auto", border: "1px solid #2B3674" }}>{jsonPayload}</pre>
            </div>
          </div>
        </div>
      )}

      {/* 2. PULL DB TAB */}
      {activeTab === "fetch" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", animation: "fadeIn 0.3s ease-out" }}>
          <div style={{ backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #0088cc" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>Full Database Migration</h4>
            <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
              Instead of pushing individual records, CamSec Ai can download your entire employee list at once via your HRMS GET API. This process handles base64 image arrays to map faces automatically.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
              <Link size={16} color="#4318FF"/> HRMS GET API URL
            </label>
            <input type="text" placeholder="https://your-hrms.com/api/get-employees" value={pullUrl} onChange={(e) => setPullUrl(e.target.value)} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", backgroundColor: "#F8FAFC" }} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
              <ShieldAlert size={16} color="#4318FF"/> Authentication Token (Optional)
            </label>
            <input type="password" placeholder="e.g. Bearer Token" value={pullToken} onChange={(e) => setPullToken(e.target.value)} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", backgroundColor: "#F8FAFC" }} />
          </div>

          <div style={{ display: "flex", gap: "15px", marginTop: "10px" }}>
            <button onClick={handleFetchStaff} disabled={fetching || !pullUrl} style={{ flex: 1, padding: "14px", backgroundColor: fetching || !pullUrl ? "#F4F7FE" : "#fff4eb", color: fetching || !pullUrl ? "#A3AED0" : "#FF912C", border: fetching || !pullUrl ? "1px solid #E2E8F0" : "1px solid #ffe4cc", borderRadius: "12px", fontWeight: "700", cursor: fetching || !pullUrl ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              <RefreshCw size={18} className={fetching ? "animate-spin" : ""} /> {fetching ? "Syncing Database..." : "Sync Database Now"}
            </button>
            <button onClick={handleSaveFetchConfig} disabled={savingFetch} style={{ flex: 1, padding: "14px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: savingFetch ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              <Save size={18} /> {savingFetch ? "Saving..." : "Save Pull Config"}
            </button>
          </div>
        </div>
      )}

      {/* 3. OUTBOUND TAB */}
      {activeTab === "outbound" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", animation: "fadeIn 0.3s ease-out" }}>
          <div style={{ backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #0088cc" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>How Outbound Sync Works</h4>
            <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
              Whenever an employee checks in via CamSec Ai facial recognition, we will automatically HTTP POST their attendance logs directly to your HRMS external Webhook URL.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
              <Link size={16} color="#4318FF"/> Destination Webhook URL
            </label>
            <input type="text" placeholder="https://your-company.com/api/hrms/attendance" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", backgroundColor: "#F8FAFC" }} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
              <ShieldAlert size={16} color="#4318FF"/> Authentication Token (Optional)
            </label>
            <input type="password" placeholder="e.g. Bearer Token" value={webhookAuthToken} onChange={(e) => setWebhookAuthToken(e.target.value)} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", backgroundColor: "#F8FAFC" }} />
          </div>

          <div style={{ backgroundColor: "#FFFFFF", padding: "16px", borderRadius: "12px", border: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h4 style={{ margin: "0 0 4px 0", color: "#2B3674", fontSize: "15px", fontWeight: "700" }}>Enable Outbound Sync</h4>
              <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>Push attendance logs automatically on check-in.</p>
            </div>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", position: "relative" }}>
              <input type="checkbox" style={{ opacity: 0, position: "absolute", width: 0, height: 0 }} checked={autoSyncOutbound} onChange={(e) => setAutoSyncOutbound(e.target.checked)} />
              <div style={{ width: "48px", height: "26px", backgroundColor: autoSyncOutbound ? "#0088cc" : "#E2E8F0", borderRadius: "20px", position: "relative", transition: "0.3s", display: "flex", alignItems: "center" }}>
                <div style={{ width: "20px", height: "20px", backgroundColor: "white", borderRadius: "50%", position: "absolute", left: autoSyncOutbound ? "25px" : "3px", transition: "0.3s" }}></div>
              </div>
            </label>
          </div>

          <button onClick={handleSaveOutbound} disabled={savingOutbound} style={{ padding: "14px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: savingOutbound ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <Save size={18} /> {savingOutbound ? "Saving..." : "Save Outbound Settings"}
          </button>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid #F4F7FE", paddingTop: "20px", marginTop: "20px" }}>
        <button onClick={onClose} style={{ padding: "12px 24px", backgroundColor: "#F4F7FE", color: "#A3AED0", border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>Close Panel</button>
      </div>
    </div>
  );
}
